import type { Team } from "../../../lib/queries/teams";

// The tabs split the teams by where they are in their life, and the split is
// a PARTITION: every team sits in exactly one of the first four, and „Všechny"
// adds them up. That is what lets the filters stay out of the tabs' way —
// nothing a filter says can contradict the tab, so filters keep their value
// across tabs and no rule has to say what carries over.
export type TeamView = "aktivni" | "ke-schvaleni" | "bez-registrace" | "pozastavene" | "vse";

export const TEAM_VIEWS: { id: TeamView; label: string }[] = [
  { id: "aktivni", label: "Aktivní" },
  { id: "ke-schvaleni", label: "Čekají na schválení" },
  { id: "bez-registrace", label: "Chybí registrace" },
  { id: "pozastavene", label: "Pozastavené" },
  { id: "vse", label: "Všechny" },
];

export const DEFAULT_VIEW: TeamView = "aktivni";

export function parseView(raw: string | null): TeamView {
  return TEAM_VIEWS.some((v) => v.id === raw) ? (raw as TeamView) : DEFAULT_VIEW;
}

// „Chybí registrace" is the server's `not_registered`: a ČNSMS club that has been
// a member and has not registered while the window is open, i.e. one that
// owes it. A cup-only club owes nothing and competes, so it stays „Aktivní".
export function viewOf(t: Team): Exclude<TeamView, "vse"> {
  if (t.status === "pending") return "ke-schvaleni";
  if (t.status === "suspended") return "pozastavene";
  if ((t.state_issues ?? []).includes("not_registered")) return "bez-registrace";
  return "aktivni";
}

export type Membership = "registrovani" | "poharovky" | "zahranicni";

export const MEMBERSHIP_OPTIONS: { id: Membership; label: string }[] = [
  { id: "registrovani", label: "Registrovaní" },
  { id: "poharovky", label: "Jen pohárovky" },
  { id: "zahranicni", label: "Zahraniční" },
];

export function parseMembership(raw: string | null): Membership | null {
  return MEMBERSHIP_OPTIONS.some((m) => m.id === raw) ? (raw as Membership) : null;
}

// Only meaningful for an „Aktivní" team; the tab decides everyone else's.
export function membershipOf(t: Team): Membership {
  if (!t.federation_is_primary) return "zahranicni";
  if ((t.state_issues ?? []).includes("cup_only")) return "poharovky";
  return "registrovani";
}

export type Outstanding = "nezaplaceno" | "chybi-foto" | "bez-cmf-id";

export const OUTSTANDING_OPTIONS: { id: Outstanding; label: string }[] = [
  { id: "nezaplaceno", label: "Nezaplaceno" },
  { id: "chybi-foto", label: "Chybí foto" },
  { id: "bez-cmf-id", label: "Členové bez ČNSMS ID" },
];

export function parseOutstanding(raw: string | null): Outstanding | null {
  return OUTSTANDING_OPTIONS.some((o) => o.id === raw) ? (raw as Outstanding) : null;
}

export function outstandingOf(t: Team): Outstanding[] {
  const issues = t.state_issues ?? [];
  const out: Outstanding[] = [];
  if (issues.includes("unpaid")) out.push("nezaplaceno");
  if (issues.includes("missing_photo")) out.push("chybi-foto");
  if ((t.members_missing_cmf_id ?? 0) > 0) out.push("bez-cmf-id");
  return out;
}

export interface TeamFilters {
  q: string;
  outstanding: Outstanding | null;
  membership: Membership | null;
}

// Name, leader and ČNSMS ID: the three things an admin knows a team by. The id
// is matched as a prefix so „37" finds the 37xxx block, not every 37 in it.
export function matchesSearch(t: Team, q: string): boolean {
  const needle = q.trim().toLocaleLowerCase("cs");
  if (!needle) return true;
  const leader = t.leader?.person;
  const leaderName = leader ? `${leader.first_name} ${leader.last_name}` : "";
  return (
    t.name.toLocaleLowerCase("cs").includes(needle) ||
    leaderName.toLocaleLowerCase("cs").includes(needle) ||
    String(t.cmf_id ?? "").startsWith(needle)
  );
}

// „K dořešení" is asked of an active ČNSMS team alone: the server computes no
// fee, photo or registration items for anyone else (fillState), so on the
// other tabs the filter could only ever empty them. It is offered, and
// applied to rows and counts, on „Aktivní" and „Všechny"; a value left in the
// URL from there neither narrows nor shows on the rest.
export function outstandingApplies(view: TeamView): boolean {
  return view === "aktivni" || view === "vse";
}

// Whether a filter the URL holds is actually in effect on this tab, so the
// empty state only blames filters the reader can see.
export function filtersInEffect(view: TeamView, f: TeamFilters): boolean {
  return (
    f.q.trim() !== "" ||
    (f.outstanding !== null && outstandingApplies(view)) ||
    (f.membership !== null && view === "aktivni")
  );
}

export function inView(t: Team, view: TeamView): boolean {
  return view === "vse" || viewOf(t) === view;
}

export function filterTeams(teams: Team[], view: TeamView, f: TeamFilters): Team[] {
  return teams.filter(
    (t) =>
      inView(t, view) &&
      matchesSearch(t, f.q) &&
      (!f.outstanding || !outstandingApplies(view) || outstandingOf(t).includes(f.outstanding)) &&
      // The membership select is offered on „Aktivní" alone.
      (!f.membership || view !== "aktivni" || membershipOf(t) === f.membership),
  );
}

// A tab's count is what the tab would show with the current filters, minus
// the one filter that splits „Aktivní" further: that select is inside the tab,
// so the tab counts all of it.
export function viewCount(teams: Team[], view: TeamView, f: TeamFilters): number {
  return filterTeams(teams, view, { ...f, membership: null }).length;
}
